/**
 * ScolaireDashboardPage — tableau de bord mairie du module Scolaire / Cantine.
 *
 * KPIs du jour (cantine + périscolaire), répartition par école, raccourcis vers
 * les écrans du module. Réservé au staff (manager/admin) ; l'agent a sa propre
 * vue terrain. Les KPIs sont aussi la source de la Vue consolidée du maire.
 */

import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Users, UtensilsCrossed, Sparkles, School, CalendarDays, ClipboardList, Upload, ChefHat } from 'lucide-react';
import { api, ApiException } from '@/lib/api';
import { Card, CardBody, KpiCard, SectionEyebrow, Alert } from '@/components/ui';
import { ScolaireLayout } from '@/components/scolaire/ScolaireLayout';
import type { ScolaireEcole, PeriscolaireEffectifJour } from '@roronoa-collectivites/shared-types';

const today = () => new Date().toISOString().slice(0, 10);

export function ScolaireDashboardPage() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [nbFamilles, setNbFamilles] = useState(0);
  const [ecoles, setEcoles] = useState<ScolaireEcole[]>([]);
  const [cantine, setCantine] = useState<{ comptes: number; absents: number }>({ comptes: 0, absents: 0 });
  const [peri, setPeri] = useState<PeriscolaireEffectifJour | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const d = today();
        const [familles, ecolesList, effCantine, effPeri] = await Promise.all([
          api.scolaire.listFamilles(),
          api.scolaire.listEcoles(),
          api.scolaire.getEffectifCantine(d).catch(() => ({ dateService: d, comptes: 0, absents: 0 })),
          api.scolaire.periscolaire.getEffectif(d).catch(() => null),
        ]);
        if (cancelled) return;
        setNbFamilles(familles.length);
        setEcoles(ecolesList);
        setCantine({ comptes: effCantine.comptes, absents: effCantine.absents });
        setPeri(effPeri);
      } catch (e) {
        if (!cancelled) setError(e instanceof ApiException ? e.message : 'Erreur de chargement');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const periTotal = peri ? peri.matin.comptes + peri.soir.comptes : 0;

  return (
    <ScolaireLayout activeTab="dashboard">
      <main className="mx-auto max-w-6xl px-4 py-6 sm:px-6">
        <div className="mb-6">
          <SectionEyebrow>Scolaire / Cantine</SectionEyebrow>
          <h1 className="font-display text-2xl font-semibold tracking-r-tight text-ink">Tableau de bord</h1>
          <p className="mt-1 text-sm text-ink-muted">Vue du jour — {new Date().toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long' })}</p>
        </div>

        {error && <Alert variant="error" className="mb-4">{error}</Alert>}

        <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
          <KpiCard
            icon={<Users className="h-5 w-5" aria-hidden="true" />}
            label="Familles inscrites"
            value={loading ? '—' : nbFamilles}
            variant="cantine"
          />
          <KpiCard
            icon={<UtensilsCrossed className="h-5 w-5" aria-hidden="true" />}
            label="Cantine aujourd'hui"
            value={loading ? '—' : cantine.comptes}
            hint={loading ? undefined : `${cantine.absents} absents`}
            hintTone="neutral"
            variant="cantine"
          />
          <KpiCard
            icon={<Sparkles className="h-5 w-5" aria-hidden="true" />}
            label="Périscolaire aujourd'hui"
            value={loading ? '—' : periTotal}
            hint={peri ? `matin ${peri.matin.comptes} · soir ${peri.soir.comptes}` : undefined}
            hintTone="neutral"
            variant="warning"
          />
          <KpiCard
            icon={<School className="h-5 w-5" aria-hidden="true" />}
            label="Écoles"
            value={loading ? '—' : ecoles.length}
            variant="success"
          />
        </div>

        {/* Répartition par école */}
        <Card className="mt-6">
          <CardBody>
            <div className="mb-3 flex items-baseline justify-between">
              <h2 className="font-display text-base font-semibold text-ink">Écoles</h2>
              <span className="text-xs text-ink-subtle">{ecoles.length} établissement{ecoles.length > 1 ? 's' : ''}</span>
            </div>
            {ecoles.length === 0 ? (
              <p className="text-sm text-ink-muted">Aucune école enregistrée. Ajoutez-en une dans l'onglet « Écoles &amp; classes ».</p>
            ) : (
              <ul className="space-y-2">
                {ecoles.map((e) => (
                  <li key={e.id} className="flex items-center justify-between rounded-r-md border border-border bg-surface/40 px-3 py-2">
                    <span className="text-sm text-ink">{e.nom}</span>
                    <span className="text-xs text-ink-subtle capitalize">{e.type}</span>
                  </li>
                ))}
              </ul>
            )}
          </CardBody>
        </Card>

        {/* Raccourcis */}
        <div className="mt-6 grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5">
          <QuickLink icon={<School className="h-5 w-5" />} label="Écoles & classes" onClick={() => navigate('/scolaire/ecoles')} />
          <QuickLink icon={<CalendarDays className="h-5 w-5" />} label="Calendrier" onClick={() => navigate('/scolaire/calendrier')} />
          <QuickLink icon={<ChefHat className="h-5 w-5" />} label="Vue cuisine" onClick={() => navigate('/scolaire/cantine')} />
          <QuickLink icon={<ClipboardList className="h-5 w-5" />} label="Planning" onClick={() => navigate('/scolaire/planning')} />
          <QuickLink icon={<Upload className="h-5 w-5" />} label="Import CSV" onClick={() => navigate('/scolaire/familles')} />
        </div>
      </main>
    </ScolaireLayout>
  );
}

function QuickLink({ icon, label, onClick }: { icon: React.ReactNode; label: string; onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="flex flex-col items-center gap-2 rounded-r-lg border border-border bg-surface/50 px-3 py-4 text-center transition-all hover:-translate-y-0.5 hover:border-cantine/40 hover:bg-cantine/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-cantine focus-visible:ring-offset-2 focus-visible:ring-offset-bg"
    >
      <span className="text-cantine-fg">{icon}</span>
      <span className="text-xs font-medium text-ink-muted">{label}</span>
    </button>
  );
}
